import React, { useContext, useEffect } from "react";
import { Switch, Route, useHistory } from "react-router-dom";
import { configStore } from "../../../contexts/ConfigContext";
import ProgressionOverview from "./ProgressionOverview/ProgressionOverview";
import Clustering from "../../Clustering/Clustering";
import StudentDetails from "./StudentDetails/StudentDetails";
import Loader from "../../../components/Loader/Loader";
import DemoBanner from "../../../components/DemoBanner/DemoBanner";
import ClassroomList from "./Classroom/List/ClassroomList";
import ClassroomDetails from "./Classroom/Details/ClassroomDetails";
// import useDashboard from "../../../hooks/useDashboard";
import { defineMessages, useIntl } from "react-intl";
import { errorStore } from "../../../contexts/ErrorContext";
import CreateGroup from "./Group/Components/CreateGroup/CreateGroup";
import GroupDetails from "./Group/Components/GroupDetails/GroupDetails";
import EditGroup from "./Group/Components/EditGroup/EditGroup";
import { useGroupsProgress } from "./Group/api/useQueries";
import { BaseRouteWithHeaderWrapper } from "../../../components/RouteWithHeaderWrapper/RouteWithHeaderWrapper";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import { useDashboardRouter } from "./routing";
import { useHasWorkmode } from "../../../hooks/useWorkmode";
import { WorkModeType } from "@evidenceb/athena-common/interfaces/Workmode";
import { UserType } from "../../../interfaces/User";
import { useDynamicDashboard } from "../../../hooks/useDashboard";

function DashboardRouter() {
    const intl = useIntl();
    const { config } = useContext(configStore);
    const history = useHistory();
    const { errorInfo, setErrorInfo } = useContext(errorStore);
    const groupsManagementFlag = useFeatureFlag("groupManagement");
    const hasSoloAIMode = useHasWorkmode(WorkModeType.SoloAI, UserType.Student);
    const { data: groups } = useGroupsProgress({
        enabled: groupsManagementFlag,
    });

    const routes = useDashboardRouter();

    const { dashboardLoaded, dashboard, dashboardQuery } =
        useDynamicDashboard();

    useEffect(() => {
        if (dashboardQuery.isError && !errorInfo.displayModal) {
            setErrorInfo({
                displayModal: true,
                modal: {
                    type: "POPUP",
                    content: {
                        title: intl.formatMessage(messages.errorTitle),
                        text: intl.formatMessage(messages.errorTxt),
                        btn: {
                            label: intl.formatMessage(messages.goToHome),
                            cancel: false,
                        },
                    },
                    onClick: () => {
                        history.push("/");
                    },
                },
            });
        }
    }, [dashboardQuery.isError, history, intl, setErrorInfo, errorInfo]);

    //console.log("dashboard_loaded:", dashboardLoaded);

    return (
        <Switch>
            <Route exact path={routes.singleClassroom(":classroomId")}>
                {config.features.demoMode?.displayIn.classDetails && (
                    <DemoBanner
                        position="right"
                        message={config.features.demoMode.message.global}
                    />
                )}
                <BaseRouteWithHeaderWrapper>
                    <Loader
                        loadedMessage={intl.formatMessage(
                            messages.dashboardLoaded
                        )}
                        isLoading={typeof dashboard === "undefined"}
                        loadingMessage={intl.formatMessage(
                            messages.loadingDashboard
                        )}
                    >
                        {dashboard && <ClassroomDetails />}
                    </Loader>
                </BaseRouteWithHeaderWrapper>
            </Route>

            {hasSoloAIMode && (
                <Route
                    exact
                    path={routes.progressionOverview(
                        ":classroomId",
                        ":moduleId"
                    )}
                >
                    {config.features.demoMode?.displayIn
                        .progressionOverview && (
                        <DemoBanner
                            position="right"
                            message={config.features.demoMode.message.global}
                        />
                    )}
                    <BaseRouteWithHeaderWrapper>
                        <Loader
                            loadedMessage={intl.formatMessage(
                                messages.dashboardLoaded
                            )}
                            isLoading={
                                // typeof groups === "undefined" &&
                                typeof dashboard === "undefined"
                            }
                            loadingMessage={intl.formatMessage(
                                messages.loadingDashboard
                            )}
                        >
                            {dashboard && (
                                <ProgressionOverview
                                    collections={dashboard.classrooms}
                                />
                            )}
                        </Loader>
                    </BaseRouteWithHeaderWrapper>
                </Route>
            )}

            {hasSoloAIMode && (
                <Route
                    exact
                    path={routes.studentDetails(
                        ":studentId",
                        ":classroomId",
                        ":moduleId?"
                    )}
                >
                    {config.features.demoMode?.displayIn.studentDetails && (
                        <DemoBanner
                            position="right"
                            message={config.features.demoMode.message.global}
                        />
                    )}
                    <BaseRouteWithHeaderWrapper>
                        <Loader
                            loadedMessage={intl.formatMessage(
                                messages.dashboardLoaded
                            )}
                            isLoading={typeof dashboard === "undefined"}
                            loadingMessage={intl.formatMessage(
                                messages.loadingDashboard
                            )}
                        >
                            {dashboard && (
                                <StudentDetails
                                    dashboard={dashboard}
                                    dashboardLoaded={dashboardLoaded}
                                />
                            )}
                        </Loader>
                    </BaseRouteWithHeaderWrapper>
                </Route>
            )}

            {config.features.clustering && (
                <Route
                    exact
                    path={routes.clustering(":classroomId", ":moduleId")}
                >
                    {config.features.demoMode?.displayIn.clustering && (
                        <DemoBanner
                            position="right"
                            message={config.features.demoMode.message.global}
                        />
                    )}
                    <BaseRouteWithHeaderWrapper>
                        <Loader
                            loadedMessage={intl.formatMessage(
                                messages.dashboardLoaded
                            )}
                            isLoading={typeof dashboard === "undefined"}
                            loadingMessage={intl.formatMessage(
                                messages.loadingDashboard
                            )}
                        >
                            {dashboard && (
                                <Clustering
                                    classrooms={dashboard.classrooms}
                                    clustering={dashboard.clustering!}
                                />
                            )}
                        </Loader>
                    </BaseRouteWithHeaderWrapper>
                </Route>
            )}

            <Route exact path={routes.createGroup}>
                <Loader
                    loadedMessage={intl.formatMessage(messages.dashboardLoaded)}
                    isLoading={typeof dashboard === "undefined"}
                    loadingMessage={intl.formatMessage(
                        messages.loadingDashboard
                    )}
                >
                    {dashboard && <CreateGroup />}
                </Loader>
            </Route>

            <Route exact path={routes.editGroup(":groupId")}>
                <Loader
                    loadedMessage={intl.formatMessage(messages.dashboardLoaded)}
                    isLoading={typeof dashboard === "undefined"}
                    loadingMessage={intl.formatMessage(
                        messages.loadingDashboard
                    )}
                >
                    {dashboard && <EditGroup />}
                </Loader>
            </Route>

            <Route exact path={routes.groupDetails(":groupId")}>
                <>
                    <BaseRouteWithHeaderWrapper>
                        <Loader
                            loadedMessage={intl.formatMessage(
                                messages.dashboardLoaded
                            )}
                            isLoading={typeof dashboard === "undefined"}
                            loadingMessage={intl.formatMessage(
                                messages.loadingDashboard
                            )}
                        >
                            {dashboard && <GroupDetails />}
                        </Loader>
                    </BaseRouteWithHeaderWrapper>
                </>
            </Route>

            {hasSoloAIMode && (
                <Route
                    exact
                    path={routes.groupProgressionOverview(
                        ":groupId",
                        ":moduleId"
                    )}
                >
                    <>
                        {config.features.demoMode?.displayIn
                            .progressionOverview && (
                            <DemoBanner
                                position="right"
                                message={
                                    config.features.demoMode.message.global
                                }
                            />
                        )}
                        <BaseRouteWithHeaderWrapper>
                            <Loader
                                loadedMessage={intl.formatMessage(
                                    messages.dashboardLoaded
                                )}
                                isLoading={typeof groups === "undefined"}
                                loadingMessage={intl.formatMessage(
                                    messages.loadingDashboard
                                )}
                            >
                                {groups && (
                                    <ProgressionOverview
                                        collections={groups}
                                        classroomsAnalytics={
                                            dashboard?.classrooms
                                        }
                                    />
                                )}
                            </Loader>
                        </BaseRouteWithHeaderWrapper>
                    </>
                </Route>
            )}

            <Route>
                {config.features.demoMode?.displayIn.classList && (
                    <DemoBanner
                        position="right"
                        message={config.features.demoMode.message.global}
                    />
                )}
                <BaseRouteWithHeaderWrapper>
                    <ClassroomList />
                </BaseRouteWithHeaderWrapper>
            </Route>
        </Switch>
    );
}

const messages = defineMessages({
    errorTitle: {
        id: "error-dashboardData-title",
        defaultMessage: "Cannot retrieve classes data",
    },
    errorTxt: {
        id: "error-dashboardData-text",
        defaultMessage:
            "An error occured while trying to retrieve your classes' data. Try again later.",
    },
    goToHome: {
        id: "errors-statements-cannotRetrieveHistory-goToHomepageButton",
        defaultMessage: "Go to homepage",
    },
    loadingDashboard: {
        id: "dashboard-loadingDashboard",
        defaultMessage: "Dashboard data is loading. Please wait.",
    },
    dashboardLoaded: {
        id: "dashboard-dashboardLoaded",
        defaultMessage: "Dashboard data is loaded",
    },
});

export default DashboardRouter;
